import React, { useState, useEffect } from "react";
import loader from "./../images/loader.gif";
import icon from "./../images/wordly_icon.png";
import "./../index.css";
import Block from "./Block";
import VersionManager from "./VersionManager";
import { firestore, auth } from "./../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDocs,
  getDoc,
  orderBy
} from "firebase/firestore";

const Dashboard = (props) => {
  const [activeTab, setActiveTab] = useState('online');
  const [online, setOnline] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsRange, setStatsRange] = useState("1d");
  const [words, setWords] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [showLinksDropdown, setShowLinksDropdown] = useState(false);
  const [wordsPool, setWordsPool] = useState({
    unused: [],
    used: [],
    lastVisible: null,
  });

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}`;
  });

  useEffect(() => {
    const onlineUsersQuery = query(
      collection(firestore, "users"),
      where("isOnline", "==", true)
    );

    const convertTimestamp = (ts) => {
      if (!ts) return null;
      if (ts.seconds) return ts.seconds * 1000;
      if (ts.toDate) return ts.toDate().getTime();
      return ts;
    };

    const unsubscribeOnline = onSnapshot(onlineUsersQuery, (snapshot) => {
      const onlineTmp = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          username: data.username,
          lastOnline: convertTimestamp(data.lastOnline),
          type: data.loginType === 0 ? "google" : data.loginType === 1 ? "apple" : "anonymous"
        };
      });
      setOnline(onlineTmp);
    });

    const unsubscribeTodaysWords = onSnapshot(
      collection(firestore, "todays_words"),
      (snapshot) => {
        const todaysWords = snapshot.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data().word;
          return acc;
        }, {});
        setWords(todaysWords);
      }
    );

    const wordsQuery = query(
      collection(firestore, "unused_words"),
      orderBy("word")
    );

    const unsubscribeWordsPool = onSnapshot(wordsQuery, async (snapshot) => {
      const unusedWords = snapshot.docs.map((doc) => ({
        id: doc.id,
        word: doc.data().word,
        status: "Unused",
      }));
      const usedWordsSnapshot = await getDocs(collection(firestore, "used_words"));
      const usedWords = usedWordsSnapshot.docs.map((doc) => ({
        id: doc.id,
        word: doc.data().word,
        status: "Used",
      }));
      setWordsPool({
        words: [...unusedWords, ...usedWords],
        lastVisible: snapshot.docs[snapshot.docs.length - 1],
      });
    });

    // Handle Statistics Fetching
    let unsubscribeStats = () => { };
    if (statsRange === "1d") {
      unsubscribeStats = onSnapshot(
        doc(firestore, "statistics", selectedDate),
        (docSnap) => {
          setStats(docSnap.exists() ? [{ date: selectedDate, ...docSnap.data() }] : null);
        }
      );
    } else {
      const rangeInDays = statsRange === "7d" ? 7 : statsRange === "30d" ? 30 : 365;
      const fetchRangeData = async () => {
        const parts = selectedDate.split("-");
        const endDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        const rangePromises = [];

        for (let i = 0; i < rangeInDays; i++) {
          const currentDate = new Date(endDate);
          currentDate.setDate(endDate.getDate() - i);
          const dateStr = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
          rangePromises.push(getDoc(doc(firestore, "statistics", dateStr)).then(snap => {
            return snap.exists() ?
              { date: dateStr, timestamp: currentDate.getTime(), ...snap.data() } :
              { date: dateStr, timestamp: currentDate.getTime(), empty: true };
          }));
        }

        const results = await Promise.all(rangePromises);
        // Sort chronologically for charts
        setStats(results.sort((a, b) => a.timestamp - b.timestamp));
      };
      fetchRangeData();
    }

    /* LIMIT WAS REMOVED EARLIER, WE CAN NOW USE A REGULAR QUERY */
    const unsubscribeAllUsers = onSnapshot(
      query(collection(firestore, "users")),
      (snapshot) => {
        const usersTmp = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            username: data.username || "Anonymous",
            email: data.emailAddress || "N/A",
            wins: data.winCount || 0,
            combo: data.comboCount || 0,
            lastOnline: convertTimestamp(data.lastOnline),
            type: data.loginType === 0 ? "google" : data.loginType === 1 ? "apple" : "anonymous",
            hasWordlyPlus: data.hasWordlyPlus === true,
          };
        });
        setAllUsers(usersTmp);
      }
    );

    setIsLoading(false);

    return () => {
      unsubscribeOnline();
      unsubscribeTodaysWords();
      unsubscribeWordsPool();
      unsubscribeStats();
      unsubscribeAllUsers();
    };
  }, [selectedDate, statsRange]);

  return isLoading ? (
    <div className="loading-overlay">
      <img src={loader} alt="Loading..." />
    </div>
  ) : (
    <div className="App">
      <header className="top-bar">
        <div className="brand">
          <img src={icon} alt="Wordly Icon" className="brand-icon" />
          <div className="brand-title">WORDLY <span>DASHBOARD</span></div>
        </div>

        <div className="daily-words-container">
          <div className="word-chip solo">
            <span className="word-label">Solo</span>
            <span className="word-value">{words?.solo || "..."}</span>
          </div>
          <div className="word-chip ad">
            <span className="word-label">Ad Word</span>
            <span className="word-value">{words?.ad || "..."}</span>
          </div>
        </div>

        <div className="top-bar-actions">
          <div className="links-dropdown-container">
            <button
              className="action-link dropdown-toggle"
              onClick={() => setShowLinksDropdown(!showLinksDropdown)}
              onBlur={() => setTimeout(() => setShowLinksDropdown(false), 200)}
            >
              <ion-icon name="link-outline"></ion-icon> External Links
              <ion-icon name={showLinksDropdown ? "chevron-up-outline" : "chevron-down-outline"}></ion-icon>
            </button>

            {showLinksDropdown && (
              <div className="links-dropdown">
                <div className="dropdown-section">
                  <span className="section-title">Developers</span>
                  <a href="https://github.com/KupermanL/Wordly-Unity" target="_blank" rel="noopener noreferrer">
                    <ion-icon name="logo-github"></ion-icon> Unity App GitHub
                  </a>
                  <a href="https://github.com/KupermanL/Wordly-Admin-Panel" target="_blank" rel="noopener noreferrer">
                    <ion-icon name="logo-github"></ion-icon> Admin Panel GitHub
                  </a>
                </div>

                <div className="dropdown-section">
                  <span className="section-title">Consoles</span>
                  <a href="https://console.firebase.google.com/u/0/project/wordle-c7049" target="_blank" rel="noopener noreferrer" className="firebase-link">
                    <ion-icon name="flame-outline"></ion-icon> Firebase Console
                  </a>
                  <a href="https://play.google.com/console/u/0/developers/5901730052861619225/app/4973090026107876534/app-dashboard" target="_blank" rel="noopener noreferrer" className="console-link">
                    <ion-icon name="desktop-outline"></ion-icon> Play Console
                  </a>
                </div>

                <div className="dropdown-section">
                  <span className="section-title">Ads (AdMob)</span>
                  <a href="https://admob.google.com/v2/apps/9205697139/overview?pli=1" target="_blank" rel="noopener noreferrer" className="admob-android-link">
                    <ion-icon name="logo-google"></ion-icon> AdMob Android
                  </a>
                  <a href="https://admob.google.com/v2/apps/2005564850/overview?pli=1" target="_blank" rel="noopener noreferrer" className="admob-ios-link">
                    <ion-icon name="logo-apple"></ion-icon> AdMob iOS
                  </a>
                </div>

                <div className="dropdown-section">
                  <span className="section-title">Stores</span>
                  <a href="https://play.google.com/store/apps/details?id=com.kupigames.wordle" target="_blank" rel="noopener noreferrer" className="store-link">
                    <ion-icon name="logo-google-playstore"></ion-icon> Play Store
                  </a>
                  <a href="https://apps.apple.com/il/app/%D7%95%D7%95%D7%A8%D7%93%D7%9C%D7%99/id1629490099" target="_blank" rel="noopener noreferrer" className="store-link" style={{ color: '#2563eb' }}>
                    <ion-icon name="logo-apple"></ion-icon> App Store
                  </a>
                </div>
              </div>
            )}
          </div>
          <button className="icon-btn logout" onClick={() => auth.signOut()} title="Sign Out">
            <ion-icon name="log-out-outline"></ion-icon>
          </button>
        </div>
      </header>

      <div className="dashboard">
        <nav className="tabs-nav">
          <button className={`tab-btn ${activeTab === 'online' ? 'active' : ''}`} onClick={() => setActiveTab('online')}>Online Users</button>
          <button className={`tab-btn ${activeTab === 'directory' ? 'active' : ''}`} onClick={() => setActiveTab('directory')}>User Directory</button>
          <button className={`tab-btn ${activeTab === 'wordPool' ? 'active' : ''}`} onClick={() => setActiveTab('wordPool')}>Word Pool</button>
          <button className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>Statistics</button>
          <button className={`tab-btn ${activeTab === 'versions' ? 'active' : ''}`} onClick={() => setActiveTab('versions')}>App Versions</button>
        </nav>

        <main className="tab-content">
          {activeTab === 'online' && <Block type="online" data={online} />}
          {activeTab === 'directory' && <Block type="directory" data={allUsers} />}
          {activeTab === 'wordPool' && <Block type="wordPool" data={wordsPool} todaysWords={words} />}
          {activeTab === 'stats' && (
            <Block
              type="stats"
              data={stats}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              statsRange={statsRange}
              setStatsRange={setStatsRange}
            />
          )}
          {activeTab === 'versions' && <VersionManager />}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
